import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { extractTotal, toPaginatedQuery } from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import {
  cabecalhoIdempotencia,
  readEnvelopeData,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  LookupRequestOptions,
  OpcoesMutacao,
} from "../financeiro-obras/financeiro-obras.gateway";
import { listMoedasProjecao } from "../financeiro-obras/financeiro-obras.projecao.gateway";
import type { MoedaOpcao } from "../financeiro-obras/financeiro-obras.projecao.types";
import type {
  CadastrarCotacaoReferenciaBody,
  CotacaoReferenciaItem,
  ListaCotacoesReferencia,
  ListarCotacoesReferenciaParams,
  SituacaoCotacaoAlterada,
  SituacaoCotacaoReferencia,
} from "./financeiro-configuracoes.moedas.types";

export const FINANCEIRO_CONFIGURACOES_PATHS = {
  COTACOES_REFERENCIA: "/api/financeiro/configuracoes/cotacoes-referencia",
  SITUACAO_COTACAO: (cotacaoId: string) =>
    `/api/financeiro/configuracoes/cotacoes-referencia/${encodeURIComponent(cotacaoId)}/situacao`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

export async function listarCotacoesReferencia(
  { moedaId, situacao, page, pageSize, sort }: ListarCotacoesReferenciaParams,
  { signal }: LookupRequestOptions = {},
): Promise<ListaCotacoesReferencia> {
  const params: Record<string, HttpQueryParam> = toPaginatedQuery({ page, pageSize, sort });
  if (moedaId) params.moedaId = moedaId;
  if (situacao) params.situacao = situacao;

  const response = await getHttpClient().get<unknown>(
    FINANCEIRO_CONFIGURACOES_PATHS.COTACOES_REFERENCIA,
    { params, signal },
  );
  const { items } = readEnvelopeData<Partial<ListaCotacoesReferencia>>(response.data);
  if (!Array.isArray(items)) throw new Error("Resposta inválida do bff.");

  return { items, total: extractTotal(response.data) ?? items.length };
}

export async function cadastrarCotacaoReferencia(
  body: CadastrarCotacaoReferenciaBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<CotacaoReferenciaItem> {
  const response = await getHttpClient().post<unknown>(
    FINANCEIRO_CONFIGURACOES_PATHS.COTACOES_REFERENCIA,
    body,
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<CotacaoReferenciaItem>(response.data);
}

export async function alterarSituacaoCotacaoReferencia(
  cotacaoId: string,
  situacao: SituacaoCotacaoReferencia,
  justificativa: string | undefined,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SituacaoCotacaoAlterada> {
  const response = await getHttpClient().patch<unknown>(
    FINANCEIRO_CONFIGURACOES_PATHS.SITUACAO_COTACAO(cotacaoId),
    justificativa === undefined ? { situacao } : { situacao, justificativa },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SituacaoCotacaoAlterada>(response.data);
}

export function listarMoedas(options: LookupRequestOptions = {}): Promise<MoedaOpcao[]> {
  return listMoedasProjecao(options);
}
